import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import './App.css';
import { API_KEY, BASE_URL } from "./config";

Modal.setAppElement('#root');

function HedgeResistanceLevel({ isOpen, closeModal, symbolData }) {
  const initialPercentValues = {
    lvl1PercentToSell: '20',
    lvl2PercentToSell: '20',
    lvl3PercentToSell: '20',
    lvl4PercentToSell: '20',
    lvl5PercentToSell: '20',
  };

  const calculateHedgeSide = (currentSide) => {
    return currentSide === 'open_long' ? 'open_short' : 'open_long';
  };


  const [formData, setFormData] = useState({
    signalRequestId: symbolData.signalRequestId || 0,
    resistanceLevelId: '',
    symbol: symbolData.symbol || '',
    side: symbolData.side || '',
    hedgeSide: '',
    resistanceLevel1: '',
    resistanceLevel2: '',
    resistanceLevel3: '',
    resistanceLevel4: '',
    resistanceLevel5: '',
    createdBy: 'signal-fly-ui',
    ...initialPercentValues,
  });

  function formatPrice(value) {
    return value >= 1 ? value.toFixed(4) : value.toFixed(8);
  }

  const [totalPercent, setTotalPercent] = useState(
    Object.values(initialPercentValues).reduce((acc, val) => acc + parseInt(val), 0)
  );
  const [percentError, setPercentError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [marketPrice, setMarketPrice] = useState('');
  const [adjustment, setAdjustment] = useState(0);
  const [submissionResult, setSubmissionResult] = useState(null);

  useEffect(() => {
    const fetchMarketPriceAndResistanceLevels = async () => {
      try {
        // Fetch market price
        const marketResponse = await fetch(`${BASE_URL}/api/bot/get/getMarkPriceBySymbol?symbol=${formData.symbol}`,
                        {
                          headers: {
                            "X-SIGNALFLY-KEY": API_KEY
                          }
                        });
        const marketData = await marketResponse.json();
        setMarketPrice(marketData.markPrice);
        const hedgeSide = calculateHedgeSide(symbolData.side);

        // Fetch resistance levels
        const resistanceResponse = await fetch(`${BASE_URL}/api/bot/ui/get/getHedgeResistanceLevel?signalRequestId=${formData.signalRequestId}&side=${formData.side}&symbol=${formData.symbol}`,
                        {
                          headers: {
                            "X-SIGNALFLY-KEY": API_KEY
                          }
                        });
        const resistanceData = await resistanceResponse.json();
        if (resistanceData && resistanceData.length > 0) {
          setFormData(resistanceData[0]);
        } else {
          // Default values if no existing resistance levels
          setFormData(fData => ({
            ...fData,
            hedgeSide: hedgeSide,
            resistanceLevel1: marketData.markPrice,
            resistanceLevel2: marketData.markPrice,
            resistanceLevel3: marketData.markPrice,
            resistanceLevel4: marketData.markPrice,
            resistanceLevel5: marketData.markPrice,
          }));
        }
      } catch (error) {
        setError(error.message);
      }
    };

    if (isOpen) {
      fetchMarketPriceAndResistanceLevels();
    }
  }, [isOpen, formData.signalRequestId, formData.symbol, formData.side, symbolData.side]);

  const handlePercentChange = (e) => {
    const { name, value } = e.target;
    const newFormData = { ...formData, [name]: value };
    const newTotalPercent = ['lvl1PercentToSell', 'lvl2PercentToSell', 'lvl3PercentToSell', 'lvl4PercentToSell', 'lvl5PercentToSell']
      .reduce((acc, lvl) => acc + parseInt(newFormData[lvl]), 0);

    if (newTotalPercent > 100) {
      setPercentError('Total PercentToSell values cannot exceed 100%.');
    } else {
      setPercentError('');
      setTotalPercent(newTotalPercent);
      setFormData(newFormData);
    }
  };

  const renderPercentDropdown = (name) => {
    return (
      <select name={name} value={formData[name]} onChange={handlePercentChange}>
        {[0, 5, 10, 20, 30, 40, 50, 75].map((percent) => (
          <option key={percent} value={percent}>
            {percent}%
          </option>
        ))}
      </select>
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAdjustmentChange = (e) => {
    setAdjustment(parseFloat(e.target.value));
    // Calculate new resistance levels based on adjustment
    setFormData(fData => ({
      ...fData,
      resistanceLevel1: formatPrice(marketPrice * (1 + adjustment)),
      resistanceLevel2: formatPrice(marketPrice * (1 + adjustment * 2)),
      resistanceLevel3: formatPrice(marketPrice * (1 + adjustment * 3)),
      resistanceLevel4: formatPrice(marketPrice * (1 + adjustment * 4)),
      resistanceLevel5: formatPrice(marketPrice * (1 + adjustment * 5)),
    }));
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    fetch(`${BASE_URL}/api/bot/ui/get/saveHedgeResistanceLevels`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        "X-SIGNALFLY-KEY": API_KEY
      },
      body: JSON.stringify(formData),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(result => {
      // Handle submission result
      setSubmissionResult(result); // Set the result
      setLoading(false);
      // Optionally, keep the modal open or close it
      // closeModal();
    })
    .catch(error => {
      setError(error.message);
    })
    .finally(() => {
      setLoading(false);
    });
  };

  const getHedgeSide = () => {
    const hedgeSide = symbolData.side === 'open_long'
      ? 'open_short'
      : 'open_long';
    return hedgeSide;
  };

  const getAdjustmentOptions = () => {
    const options = getHedgeSide() === 'open_long'
      ? [0,1,2,3,4,5,10, 25, 50, 75, 100]
      : [0, -1,-2,-3,-4,-5,-10,-25, -50, -75, -100];
    return options.map(option => (
      <option key={option} value={option / 1000}>
        {option}%
      </option>
    ));
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      contentLabel="Hedge Resistance Level Parameters"
      className="ReactModal__Content modal-content"
      overlayClassName="ReactModal__Overlay"
    >
      <button className="modal-close-button" onClick={closeModal}>&times;</button>
      <h2 className="page-title">Hedge Resistance Level Parameters</h2>
      <br/>
      {loading && <p className="loading-message">Loading...</p>}
      {error && <p>Error: {error}</p>}
      {submissionResult && <div>Submission Result: {JSON.stringify(submissionResult)}</div>}
      {!loading && !submissionResult && (
      <form className="modal-form" onSubmit={handleSubmit}>
      <label>
          Signal Request ID:
          <input type="text" value={formData.signalRequestId} readOnly />
        </label>
        <label>
          Resistance Level ID:
          <input type="text" value={formData.resistanceLevelId} readOnly />
        </label>
        <label>
          Symbol:
          <input type="text" value={formData.symbol} readOnly />
        </label>
        <label>
          Side:
          <input type="text" value={formData.side} readOnly />
        </label>
        <label>
          Hedge Side:
          <input type="text" value={formData.hedgeSide} readOnly />
        </label>
        {['resistanceLevel1', 'resistanceLevel2', 'resistanceLevel3', 'resistanceLevel4', 'resistanceLevel5'].map((key, index) => (
        <div key={key}>
          <label>
            {key}:
            <input
              type="text"
              name={key}
              value={formData[key]}
              onChange={handleInputChange}
            />
          </label>
          <label>
            {`lvl${index + 1}PercentToSell`}:
            {renderPercentDropdown(`lvl${index + 1}PercentToSell`)}
          </label>
        </div>
      ))}

        <label>
          Current Market Price:
          <input type="text" value={marketPrice} readOnly />
        </label>
        <label>
          Adjust Percentage:
          <select value={adjustment} onChange={handleAdjustmentChange}>
          {getAdjustmentOptions()}
          </select>
        </label>
        <br/>
        {percentError && <p style={{ color: '#DC143C', backgroundColor: '#F5FFFA', padding: '10px' }}>Error: {percentError} </p>}
        <div className="modal-button-container">
          <button type="submit" disabled={loading}>Submit</button>
          <button type="button" onClick={closeModal}>Cancel</button>
        </div>
      </form>
      )}
    </Modal>
  );
}

export default HedgeResistanceLevel;
